import React, { useContext, useEffect, useRef, useState } from 'react';
import { Issue } from 'api/api.types';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useVisibilityObserver } from '@ktag/styleguide/dist/react';

import VoterInfos from 'lib/VoterInfos/VoterInfos';
import getIssueType, { ISSUE_FILTER_TYPES } from 'utils/getIssueType';
import CountedCircles from 'lib/CountedCircles/CountedCircles';
import ApiContext from 'api/ApiContext';
import useSetStickyProgBar from 'components/poll/useSetStickyProgBar';
import ProgressBarHideOnSticky from './ProgressBarHideOnSticky';

const NATIONAL_COUNCIL_YEAR = 2010;
const LIVE_API_YEAR = 2018;
const COUNCIL_OF_STATES_YEAR = 2019;

interface Props {
  issue: Issue;
  showVoterInfos?: boolean;
  hideCountedCircles?: boolean;
}
const ProgressBar = ({ issue, showVoterInfos, hideCountedCircles }: Props) => {
  const { t } = useTranslation('wap');
  const progressbarRef = useRef<HTMLDivElement>();

  useSetStickyProgBar(Boolean(issue));

  const isVisible = useVisibilityObserver(progressbarRef);
  const { getAggregatingDomains } = useContext(ApiContext);

  const shouldDisplayDistrics =
    issue?.info?.type === 'election_biproportional_apportionment';

  const query = useQuery(
    ['aggregating_domains', issue?.info?.id],
    () => getAggregatingDomains(issue?.info?.id),
    {
      enabled: shouldDisplayDistrics,
    },
  );

  const [showShimmer, setShowShimmer] = useState(false);
  useEffect(() => {
    // wait for loading animation to finish, before displaying shimmer
    setTimeout(() => {
      setShowShimmer(true);
    }, 2000);
  }, []);

  if (!issue) {
    return null;
  }

  let totalCountingCircles = issue?.progress?.total_number_of_counting_circles;
  let finishedCountingCircles = issue?.progress?.number_of_finished_counting_circles;
  const eligibleVoters = issue?.results?.eligible_voters;
  const participation = issue?.results?.participation;
  let isFinal = issue?.progress?.final;
  const year = parseInt(issue?.info.date.match(/(\d{4})/)?.[0], 10);

  const isNationalPoll =
    getIssueType(issue?.info) === ISSUE_FILTER_TYPES.poll &&
    issue.info.scope === 'federal';

  const isNationalCouncil =
    getIssueType(issue?.info) === ISSUE_FILTER_TYPES.nationalCouncil;

  const isCouncilOfStates =
    getIssueType(issue?.info) === ISSUE_FILTER_TYPES.governmentCouncil &&
    issue.info.title.de.toLowerCase().includes('stände');

  const hasVotersAbroad =
    // national polls allow voters abroad since 2010.
    // However, in our archive data we have already cleaned the number of countingCirlces for polls
    (isNationalPoll && year >= LIVE_API_YEAR) ||
    // national council elections allow voters abroad since 2010.
    // counctingCircles for elections are not cleaned in our Archive data
    (isNationalCouncil && year >= NATIONAL_COUNCIL_YEAR) ||
    // council of states elections allow voters abroad since 2019.
    (isCouncilOfStates && year >= COUNCIL_OF_STATES_YEAR);

  if (hasVotersAbroad) {
    totalCountingCircles -= 1;

    // when eligible_voters_living_abroad does not exist in the data,
    // voters abroad have not yet been counted. Therefore we must not reduce by 1
    if (issue?.results?.eligible_voters_living_abroad) {
      finishedCountingCircles -= 1;
    }
  }

  // ensure we don't get negative numbers by the substracting above

  // For Grossratswahlen, we need to display the progress of the districts instead of the municipalities
  // which has to be loaded from the aggregating_domains endpoint
  if (shouldDisplayDistrics) {
    totalCountingCircles = query.data?.data.records?.length;
    finishedCountingCircles = query.data?.data.records?.filter(
      ({ finished }) => finished,
    ).length;
    isFinal = finishedCountingCircles === totalCountingCircles;
  }

  const hasCountStarted = finishedCountingCircles > 0;
  const cleanFinishedCountingCircles = Math.max(finishedCountingCircles, 0);
  const cleanTotalCountingCircles = Math.max(totalCountingCircles, 0);

  const progress = (cleanFinishedCountingCircles / cleanTotalCountingCircles) * 100;

  const countedCirclesI18nKey = !shouldDisplayDistrics
    ? 'progress_counted_circles'
    : 'progress_counted_circles_districts';

  const getProgressLabel = () => {
    if (progress === 0) {
      return <>{t('progress_not_started')}</>;
    }

    if (!progress) {
      return null;
    }

    if (isFinal) {
      return (
        <>
          <strong>{t('progress_finished', { total: cleanTotalCountingCircles })}</strong>
          <span>{t(countedCirclesI18nKey)}</span>
        </>
      );
    }

    return (
      <>
        <strong>
          {t('progress_ongoing', {
            finished: cleanFinishedCountingCircles,
            total: cleanTotalCountingCircles,
          })}
        </strong>
        <span>{t(countedCirclesI18nKey, { count: cleanTotalCountingCircles })}</span>
      </>
    );
  };

  const progressLabel = getProgressLabel();

  const isBiProportional = issue.info.type === 'election_biproportional_apportionment';
  const showCountedCircles =
    hasCountStarted && !isFinal && isBiProportional && !hideCountedCircles;

  return (
    <div className="progressbar">
      {progressLabel && <div className="progressbar__count">{progressLabel}</div>}
      <ProgressBarHideOnSticky>
        {showCountedCircles && <CountedCircles electionId={issue.info.id} />}
      </ProgressBarHideOnSticky>
      <div className="progressbar__progress">
        <div
          className={isVisible ? 'progressbar__progress-inner' : ''}
          style={{ width: `${progress}%` }}
          ref={progressbarRef}
        />
        <div
          className="progressbar__progress-placeholder"
          style={{ width: `${100 - progress}%` }}
        />
        {showShimmer && !isFinal && <div className="progressbar__progress-shimmer" />}
      </div>
      <ProgressBarHideOnSticky>
        {showVoterInfos && eligibleVoters && participation && (
          <VoterInfos eligibleVoters={eligibleVoters} participation={participation} />
        )}
      </ProgressBarHideOnSticky>
    </div>
  );
};

export default ProgressBar;
